import { useCallback, useState } from "react";
import axios from "axios";
import toast from "react-hot-toast";
import { CART_MODULE, SURGE_PRICING_APIS } from "@/api_framework/api_config";
import {
  ISurgeItemRef,
  ISurgePreviewData,
  ISurgePriceCheckResult,
  ISurgeResponse,
  ISurgeRule,
  ISurgeRuleBody,
  ISurgeRuleFilters,
  ISurgeRuleListData,
} from "@/api_framework/api_modals/SurgePricing";
import { useConfiguration } from "@/app/hooks/useConfiguration";
import { buildAuthHeaders, readErrorMessage } from "../quick_commerce/homeTileApiUtils";

export interface ISurgeSearchItem extends ISurgeItemRef {
  price?: number;
}

const SEARCH_TAG_TO_TYPE: Record<string, ISurgeItemRef["item_type"]> = { "#service": "SERVICE", "#bom": "BOM" };

export const useSurgePricingAPI = () => {
  const configuration = useConfiguration();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const headers = useCallback(
    () => buildAuthHeaders(configuration.localStorage.ACCESS_TOKEN.key),
    [configuration.localStorage.ACCESS_TOKEN.key],
  );

  // Runs a call, toasts the server message on failure, returns data or null.
  const call = useCallback(async <T,>(request: () => Promise<{ data: ISurgeResponse<T> }>, fallback: string) => {
    setIsLoading(true);
    try {
      const res = await request();
      if (!res.data?.status) {
        toast.error(res.data?.message || fallback);
        return null;
      }
      return res.data.data;
    } catch (error) {
      toast.error(readErrorMessage(error) || fallback);
      return null;
    } finally {
      setIsLoading(false);
    }
  }, []);

  const listRules = useCallback(
    (filters: ISurgeRuleFilters) => {
      const params = Object.fromEntries(Object.entries(filters).filter(([, v]) => v !== "" && v != null));
      return call<ISurgeRuleListData>(
        () => axios.get(SURGE_PRICING_APIS.RULES, { headers: headers(), params }),
        "Couldn't load rules.",
      );
    },
    [call, headers],
  );

  const listPersonas = useCallback(
    () => call<string[]>(() => axios.get(SURGE_PRICING_APIS.PERSONAS, { headers: headers() }), "Couldn't load personas."),
    [call, headers],
  );

  const getRule = useCallback(
    (ruleId: string) =>
      call<ISurgeRule>(() => axios.get(`${SURGE_PRICING_APIS.RULES}/${ruleId}`, { headers: headers() }), "Rule not found."),
    [call, headers],
  );

  // All active services + BOMs (no clinic) for the rule item pickers.
  const saveRule = useCallback(
    (body: ISurgeRuleBody, ruleId?: string) =>
      call<ISurgeRule>(
        () =>
          ruleId
            ? axios.put(`${SURGE_PRICING_APIS.RULES}/${ruleId}`, body, { headers: headers() })
            : axios.post(SURGE_PRICING_APIS.RULES, body, { headers: headers() }),
        "Couldn't save the rule.",
      ),
    [call, headers],
  );

  const previewRule = useCallback(
    (rule: ISurgeRuleBody, clinicId: string, items: ISurgeItemRef[]) =>
      call<ISurgePreviewData>(
        () => axios.post(SURGE_PRICING_APIS.PREVIEW, { rule, clinic_id: clinicId, items }, { headers: headers() }),
        "Couldn't build the preview.",
      ),
    [call, headers],
  );

  const priceCheck = useCallback(
    (body: {
      clinic_id: string;
      service_type_id: string;
      slot_start: string;
      item: ISurgeItemRef;
      persona_tags: string[];
    }) =>
      call<ISurgePriceCheckResult>(
        () => axios.post(SURGE_PRICING_APIS.PRICE_CHECK, body, { headers: headers() }),
        "Couldn't check the price.",
      ),
    [call, headers],
  );

  // Service + BOM search reuses the cart search (clinic-scoped).
  const searchItems = useCallback(
    async (clinicId: string, keyword: string): Promise<ISurgeSearchItem[]> => {
      if (!clinicId || keyword.trim().length < 3) return [];
      const params = new URLSearchParams({ clinic_id: clinicId, search_keyword: keyword.trim() });
      params.append("search_tag", "Services");
      params.append("search_tag", "BOM");
      try {
        const res = await axios.get(CART_MODULE.CART_SEARCH.baseURL, { params, headers: headers() });
        const rows: { id: string; name: string; tag: string; price?: number }[] = res.data?.data ?? [];
        return rows
          .filter((row) => SEARCH_TAG_TO_TYPE[row.tag])
          .map((row) => ({ item_id: row.id, item_type: SEARCH_TAG_TO_TYPE[row.tag], name: row.name, price: row.price }));
      } catch {
        return [];
      }
    },
    [headers],
  );

  return { isLoading, listRules, listPersonas, getRule, saveRule, previewRule, priceCheck, searchItems };
};
